import React, { useState, useEffect, useMemo } from 'react';
import { 
    ShieldExclamationIcon, 
    ArrowPathIcon, 
    PlusIcon, 
    ExclamationTriangleIcon,
    CheckCircleIcon,
    FlagIcon,
    XMarkIcon,
    MagnifyingGlassIcon,
    BoltIcon,
    GlobeAltIcon,
    FireIcon,
} from '@heroicons/react/24/outline';
import type { SrmProject, RiskEntry, SrmAlert } from '@types';
import { visibleSrmRisks } from '../common/srmClientView';
import { formatLocalDateTime } from '../utils/localDateTime';

interface SrmRiskRegisterProps {
    project?: SrmProject;
    risks?: RiskEntry[];
    organizationId?: string;
    isGlobal?: boolean;
    alerts?: SrmAlert[];
    onUpdate?: (risks: RiskEntry[]) => void;
    onLaunchTool?: (recommendationTag: string) => void;
    onOpenRemediation?: (risk: RiskEntry) => void;
    onUpdateLifecycle?: (riskId: string, status: RiskEntry['status']) => Promise<void>;
    title?: string;
    description?: string;
    readOnly?: boolean;
}

// ─── Comprehensive Risk Library ───────────────────────────────────────────────

const PREDEFINED_RISKS = [
    // ── GEOPOLITICAL ──────────────────────────────────────────────────────────
    {
        threat: 'Civil Unrest & Protest Escalation',
        category: 'Geopolitical',
        likelihood: 'High',
        impact: 'High',
        score: 16,
        mitigation: 'Implement staggered evacuation protocols and increase perimeter security. Monitor social media and local news for escalation indicators.',
        recommendation: 'Run Geopolitical Analysis to monitor local sentiment.'
    },
    {
        threat: 'Political Instability & Government Transition',
        category: 'Geopolitical',
        likelihood: 'Medium',
        impact: 'High',
        score: 12,
        mitigation: 'Engage local liaison officers, maintain relationships with multiple political stakeholders, and prepare contingency operating plans.',
        recommendation: 'Run Country Geopolitical & Risk Analysis for trend assessment.'
    },
    {
        threat: 'Regulatory & Sanctions Exposure',
        category: 'Geopolitical',
        likelihood: 'Low',
        impact: 'Critical',
        score: 14,
        mitigation: 'Conduct quarterly compliance reviews with local legal counsel and maintain a sanctions watch list for all third-party vendors.',
        recommendation: 'Run Information Highway Audit for compliance mapping.'
    },

    // ── CONFLICT & SECURITY ──────────────────────────────────────────────────
    {
        threat: 'Armed Conflict & Active Hostilities',
        category: 'Conflict',
        likelihood: 'Medium',
        impact: 'Critical',
        score: 20,
        mitigation: 'Activate Armed Conflict Standard Operating Procedures. Establish safe rooms, evacuation routes, and hostile environment first aid resources.',
        recommendation: 'Run EP Threat Assessment for personnel safety posture.'
    },
    {
        threat: 'Terrorism & Mass Casualty Bombing',
        category: 'Conflict',
        likelihood: 'Low',
        impact: 'Critical',
        score: 16,
        mitigation: 'Implement counter-IED awareness training, standoff distance protocols, vehicle search procedures, and hostile act reporting channels.',
        recommendation: 'Run Crisis Management Plan for emergency response.'
    },
    {
        threat: 'IED / VBIED Threat to Facilities',
        category: 'Conflict',
        likelihood: 'Low',
        impact: 'Critical',
        score: 14,
        mitigation: 'Enforce vehicle exclusion zones, install jersey barriers, conduct regular IED sweep protocols and train security personnel in counter-IED.',
        recommendation: 'Run FSAT (Facility Security Audit Tool) for vulnerability mapping.'
    },
    {
        threat: 'Armed Robbery & Vehicle Ambush',
        category: 'Conflict',
        likelihood: 'Medium',
        impact: 'High',
        score: 12,
        mitigation: 'Implement low-profile travel protocols, route risk assessments, armored vehicle use for high-risk routes, and convoy buddy systems.',
        recommendation: 'Run EP Threat Assessment for route security analysis.'
    },
    {
        threat: 'Militant / Insurgent Activity in Area of Operations',
        category: 'Conflict',
        likelihood: 'Medium',
        impact: 'High',
        score: 15,
        mitigation: 'Establish pattern-of-life monitoring, local community early-warning networks, and pre-identified casualty collection points.',
        recommendation: 'Run Country Geopolitical & Risk Analysis for insurgent mapping.'
    },
    {
        threat: 'Kidnap & Ransom (Staff / Third-Party)',
        category: 'Conflict',
        likelihood: 'Low',
        impact: 'Critical',
        score: 13,
        mitigation: 'Commission crisis response retainer, implement personnel tracking, enforce travel authorization procedures, and conduct K&R awareness briefings.',
        recommendation: 'Run EP Threat Assessment for personnel safety.'
    },
    {
        threat: 'Hostage-Taking / Siege Scenario',
        category: 'Conflict',
        likelihood: 'Low',
        impact: 'Critical',
        score: 14,
        mitigation: 'Develop and rehearse facility lockdown and barricade protocols. Establish a dedicated crisis communications channel with law enforcement.',
        recommendation: 'Run Crisis Management Plan for siege-response procedures.'
    },

    // ── NATURAL DISASTER ─────────────────────────────────────────────────────
    {
        threat: 'Earthquake & Seismic Activity',
        category: 'Natural Disaster',
        likelihood: 'Medium',
        impact: 'Critical',
        score: 18,
        mitigation: 'Conduct structural assessments, install seismic early-warning systems, train staff in Drop-Cover-Hold-On procedures, and stockpile emergency supplies.',
        recommendation: 'Run Emergency Preparedness Plan for seismic response.'
    },
    {
        threat: 'Flooding & Flash Flood Event',
        category: 'Natural Disaster',
        likelihood: 'High',
        impact: 'High',
        score: 16,
        mitigation: 'Install flood barriers and early-warning sensors, relocate critical assets above flood lines, and establish elevated assembly points.',
        recommendation: 'Run Business Resiliency Plan for flood continuity.'
    },
    {
        threat: 'Tropical Cyclone / Hurricane / Typhoon',
        category: 'Natural Disaster',
        likelihood: 'Medium',
        impact: 'Critical',
        score: 17,
        mitigation: 'Implement a 72-hour pre-landfall evacuation trigger, reinforce structures to wind ratings, and pre-position recovery equipment.',
        recommendation: 'Run Emergency Preparedness Plan for severe weather.'
    },
    {
        threat: 'Wildfire & Uncontrolled Burning',
        category: 'Natural Disaster',
        likelihood: 'Medium',
        impact: 'High',
        score: 12,
        mitigation: 'Maintain defensible space around all facilities, install ember-resistant venting, develop evacuation trigger criteria tied to fire rating indices.',
        recommendation: 'Run Emergency Preparedness Plan for wildfire evacuation.'
    },
    {
        threat: 'Extreme Heat & Heatwave Event',
        category: 'Natural Disaster',
        likelihood: 'High',
        impact: 'Medium',
        score: 10,
        mitigation: 'Establish heat stress thresholds, redistribute outdoor work to cooler hours, provide shaded rest areas and hydration stations.',
        recommendation: 'Run Emergency Preparedness Plan for workforce health.'
    },
    {
        threat: 'Drought & Freshwater Shortage',
        category: 'Natural Disaster',
        likelihood: 'Medium',
        impact: 'Medium',
        score: 9,
        mitigation: 'Audit water dependency across operations, establish water storage reserves, and pre-contract emergency water supply agreements.',
        recommendation: 'Run Business Resiliency Plan for resource continuity.'
    },
    {
        threat: 'Volcanic Eruption & Ash Cloud',
        category: 'Natural Disaster',
        likelihood: 'Low',
        impact: 'Critical',
        score: 13,
        mitigation: 'Monitor volcanic alert levels from national geological agencies and maintain asset relocation procedures for ash fallout scenarios.',
        recommendation: 'Run Emergency Preparedness Plan for geological hazards.'
    },
    {
        threat: 'Tsunami & Coastal Inundation',
        category: 'Natural Disaster',
        likelihood: 'Low',
        impact: 'Critical',
        score: 14,
        mitigation: 'Identify and drill tsunami evacuation routes to high ground, install audible tsunami warning sirens, avoid siting critical assets in inundation zones.',
        recommendation: 'Run Emergency Preparedness Plan for coastal hazards.'
    },
    {
        threat: 'Epidemic / Pandemic & Mass Illness',
        category: 'Natural Disaster',
        likelihood: 'Medium',
        impact: 'High',
        score: 12,
        mitigation: 'Develop a pandemic response plan covering remote working, PPE stockpiling, disinfection protocols, and critical function succession planning.',
        recommendation: 'Run Business Resiliency Plan for health-crisis continuity.'
    },

    // ── PHYSICAL ─────────────────────────────────────────────────────────────
    {
        threat: 'Physical Breach of Perimeter',
        category: 'Physical',
        likelihood: 'Medium',
        impact: 'Critical',
        score: 15,
        mitigation: 'Upgrade biometric access controls, install anti-climb fencing and CCTV analytics, and increase guard-force response drills.',
        recommendation: 'Run FSAT (Facility Security Audit Tool) for vulnerability mapping.'
    },
    {
        threat: 'Facility Fire & Emergency Evacuation',
        category: 'Physical',
        likelihood: 'Medium',
        impact: 'High',
        score: 12,
        mitigation: 'Conduct bi-annual fire drills, maintain suppression systems, ensure clear egress routes and trained fire wardens on every floor.',
        recommendation: 'Run Emergency Preparedness Plan for fire evacuation.'
    },

    // ── CYBER ─────────────────────────────────────────────────────────────────
    {
        threat: 'Data Exfiltration / Insider Threat',
        category: 'Cyber',
        likelihood: 'Low',
        impact: 'Critical',
        score: 10,
        mitigation: 'Implement Zero-Trust architecture, DLP monitoring, privileged access management, and mandatory off-boarding procedures.',
        recommendation: 'Run Information Highway Audit for data flow verification.'
    },
    {
        threat: 'Ransomware Attack on Operational Systems',
        category: 'Cyber',
        likelihood: 'Medium',
        impact: 'Critical',
        score: 16,
        mitigation: 'Maintain immutable offline backups, enforce endpoint detection and response solutions, and conduct monthly phishing simulation exercises.',
        recommendation: 'Run Information Highway Audit for system exposure.'
    },
    {
        threat: 'Critical Infrastructure Cyber Attack (OT/SCADA)',
        category: 'Cyber',
        likelihood: 'Low',
        impact: 'Critical',
        score: 14,
        mitigation: 'Air-gap or segment OT networks, enforce ICS-specific patch management, and deploy anomaly detection on industrial control systems.',
        recommendation: 'Run FSAT for ICS/SCADA vulnerability audit.'
    },

    // ── PEOPLE ────────────────────────────────────────────────────────────────
    {
        threat: 'Executive Kidnap & Ransom (K&R)',
        category: 'People',
        likelihood: 'Low',
        impact: 'Critical',
        score: 12,
        mitigation: 'Implement mandatory executive protection detail, GPS tracking for senior staff, and retain a K&R crisis consultancy on standby.',
        recommendation: 'Run EP Threat Assessment for personnel safety.'
    },
    {
        threat: 'Staff Mass Casualty Event',
        category: 'People',
        likelihood: 'Very Low',
        impact: 'Critical',
        score: 13,
        mitigation: 'Train designated first responders in mass-casualty triage, pre-position trauma kits, and establish casualty collection points.',
        recommendation: 'Run Crisis Management Plan for mass-casualty response.'
    },

    // ── OPERATIONAL ──────────────────────────────────────────────────────────
    {
        threat: 'Supply Chain Interruption',
        category: 'Operational',
        likelihood: 'Medium',
        impact: 'Medium',
        score: 9,
        mitigation: 'Diversify vendor base across geographies, maintain critical spares inventory, and establish pre-negotiated emergency supplier agreements.',
        recommendation: 'Run Business Resiliency Plan for continuity mapping.'
    },
    {
        threat: 'Critical Infrastructure Failure (Power / Water)',
        category: 'Operational',
        likelihood: 'Medium',
        impact: 'High',
        score: 12,
        mitigation: 'Install UPS and diesel generator backup with 72-hour fuel reserves, negotiate priority restoration agreements with utility providers.',
        recommendation: 'Run Business Resiliency Plan for infrastructure resilience.'
    },
    {
        threat: 'Telecommunications & Connectivity Outage',
        category: 'Operational',
        likelihood: 'Medium',
        impact: 'Medium',
        score: 9,
        mitigation: 'Deploy redundant ISP links and satellite communications backup. Establish out-of-band command networks for crisis communications.',
        recommendation: 'Run Information Highway Audit for comms resilience.'
    },
];

const CATEGORIES = ['All', 'Conflict', 'Natural Disaster', 'Geopolitical', 'Cyber', 'Physical', 'People', 'Operational'];

const CATEGORY_CONFIG: Record<string, { color: string; icon: React.ReactNode }> = {
    'Conflict':         { color: 'border-red-400 bg-red-50 text-red-700',    icon: <ShieldExclamationIcon className="w-4 h-4" /> },
    'Natural Disaster': { color: 'border-orange-400 bg-orange-50 text-orange-700', icon: <BoltIcon className="w-4 h-4" /> },
    'Geopolitical':     { color: 'border-violet-400 bg-violet-50 text-violet-700', icon: <GlobeAltIcon className="w-4 h-4" /> },
    'Cyber':            { color: 'border-blue-400 bg-blue-50 text-blue-700',  icon: <FireIcon className="w-4 h-4" /> },
    'Physical':         { color: 'border-slate-400 bg-slate-50 text-slate-700', icon: <ShieldExclamationIcon className="w-4 h-4" /> },
    'People':           { color: 'border-pink-400 bg-pink-50 text-pink-700',  icon: <ExclamationTriangleIcon className="w-4 h-4" /> },
    'Operational':      { color: 'border-amber-400 bg-amber-50 text-amber-700', icon: <FlagIcon className="w-4 h-4" /> },
};

// ─── Component ────────────────────────────────────────────────────────────────

const SrmRiskRegister: React.FC<SrmRiskRegisterProps> = ({ 
    project, 
    risks: initialRisks, 
    organizationId, 
    isGlobal, 
    alerts = [], 
    onUpdate, 
    onLaunchTool,
    onOpenRemediation,
    onUpdateLifecycle,
    title,
    description,
    readOnly = false
}) => {
    const [risks, setRisks] = useState<RiskEntry[]>(initialRisks || project?.risks || []);

    useEffect(() => {
        if (initialRisks) {
            setRisks(initialRisks);
        } else if (project?.risks) {
            setRisks(project.risks);
        }
    }, [initialRisks, project?.risks]);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [busyRiskId, setBusyRiskId] = useState<string | null>(null);
    const [lifecycleError, setLifecycleError] = useState('');
    const [modalCategory, setModalCategory]   = useState<string>('All');
    const [searchQuery, setSearchQuery]       = useState('');

    const handleAddRisk = (predefined: typeof PREDEFINED_RISKS[0]) => {
        const newRisk: RiskEntry = {
            id: `R-${predefined.category.substring(0, 3).toUpperCase()}-${Math.floor(Math.random() * 9000) + 1000}`,
            threat: predefined.threat,
            category: predefined.category as any,
            likelihood: predefined.likelihood as any,
            impact: predefined.impact as any,
            score: predefined.score,
            mitigation: predefined.mitigation,
            owner: 'SRM Manager',
            status: 'Identified',
            recommendation: predefined.recommendation
        };

        const updatedRisks = [newRisk, ...risks];
        setRisks(updatedRisks);
        setIsAddModalOpen(false);
        setSearchQuery('');
        if (onUpdate) onUpdate(updatedRisks);
    };

    const filteredLibrary = useMemo(() => {
        return PREDEFINED_RISKS.filter(r => {
            const matchCat = modalCategory === 'All' || r.category === modalCategory;
            const q = searchQuery.toLowerCase();
            const matchSearch = !q || r.threat.toLowerCase().includes(q) || r.category.toLowerCase().includes(q);
            return matchCat && matchSearch;
        });
    }, [modalCategory, searchQuery]);

    const getScoreColor = (score: number) => {
        if (score >= 15) return 'text-red-600 bg-red-50 border-red-200';
        if (score >= 10) return 'text-amber-600 bg-amber-50 border-amber-200';
        return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    };

    const visibleRisks = visibleSrmRisks(risks, readOnly);
    const pendingRisks = visibleRisks.filter(r => r.status === 'Pending');
    const activeRisks  = visibleRisks.filter(r => r.status !== 'Pending');
    const showLifecycle = !readOnly || Boolean(onUpdateLifecycle);

    const handleApproveRisk = (id: string) => {
        const updated = risks.map(r => r.id === id ? { ...r, status: 'Identified' } as RiskEntry : r);
        setRisks(updated);
        if (onUpdate) onUpdate(updated);
    };

    const handleUpdateStatus = async (id: string, newStatus: RiskEntry['status']) => {
        if (readOnly) {
            if (!onUpdateLifecycle)
                return;
            setBusyRiskId(id);
            setLifecycleError('');
            try {
                await onUpdateLifecycle(id, newStatus);
                setRisks(current => current.map(risk => risk.id === id ? { ...risk, status: newStatus } : risk));
            } catch (error: any) {
                setLifecycleError(error?.message || 'Risk status could not be saved.');
            } finally {
                setBusyRiskId(null);
            }
            return;
        }
        const updated = risks.map(r => r.id === id ? { ...r, status: newStatus, updatedAt: new Date().toISOString() } as RiskEntry : r);
        setRisks(updated);
        if (onUpdate) onUpdate(updated);
    };

    const handleDiscardRisk = (id: string) => {
        const updated = risks.filter(r => r.id !== id);
        setRisks(updated);
        if (onUpdate) onUpdate(updated);
    };

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            {/* ── Header ── */}
            <div className="p-6 border-b border-slate-100 bg-slate-50/50">
                {lifecycleError && <p role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">{lifecycleError}</p>}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <ShieldExclamationIcon className="w-5 h-5 text-slate-900" />
                            <h3 className="text-lg font-bold text-slate-900 uppercase tracking-tight">
                                {title || "Active Risk Register"}
                            </h3>
                            {isGlobal && (
                                <span className="bg-indigo-100 text-indigo-700 text-[10px] font-black px-2 py-0.5 rounded tracking-widest uppercase">Global Master</span>
                            )}
                        </div>
                        <p className="text-sm text-slate-500">
                            {description || "Framework-specific threat matrix and mitigation tracking."}
                        </p>
                    </div>
                    {!readOnly && <div className="flex items-center gap-2">
                        <button
                            onClick={() => { setIsAddModalOpen(true); setModalCategory('All'); setSearchQuery(''); }}
                            className="flex items-center gap-2 px-6 py-2.5 text-sm font-black text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 uppercase tracking-widest"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Add Risk
                        </button>
                    </div>}
                </div>

                {/* Score Summary */}
                <div className={`grid grid-cols-2 gap-4 mt-6 ${readOnly ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
                    <div className="bg-white p-4 rounded-xl border border-slate-200">
                        <span className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Active Risks</span>
                        <span className="text-2xl font-bold text-slate-900">{activeRisks.length}</span>
                    </div>
                    <div className="bg-white p-4 rounded-xl border border-slate-200">
                        <span className="block text-[10px] font-black text-red-400 uppercase tracking-widest mb-1">Critical / High</span>
                        <span className="text-2xl font-bold text-red-600">{activeRisks.length ? activeRisks.filter(r => r.score >= 15).length : '—'}</span>
                    </div>
                    {!readOnly && <div className="bg-white p-4 rounded-xl border border-slate-200 group relative cursor-help">
                        {pendingRisks.length > 0 && (
                            <div className="absolute -top-2 -right-2 bg-blue-600 text-white text-[10px] font-black px-2 py-0.5 rounded-full animate-bounce z-10 shadow-lg border border-white">
                                {pendingRisks.length} NEW
                            </div>
                        )}
                        <span className="block text-[10px] font-black text-blue-400 uppercase tracking-widest mb-1">Pending Review</span>
                        <span className="text-2xl font-bold text-blue-600">{pendingRisks.length}</span>
                    </div>}
                    <div className="bg-white p-4 rounded-xl border border-slate-200">
                        <span className="block text-[10px] font-black text-emerald-400 uppercase tracking-widest mb-1">Mitigated</span>
                        <span className="text-2xl font-bold text-emerald-600">{activeRisks.length ? activeRisks.filter(r => r.status === 'Mitigated' || r.status === 'Closed').length : '—'}</span>
                    </div>
                </div>
            </div>

            {/* ── Pending (AI-detected) Risks ── */}
            {pendingRisks.length > 0 && (
                <div className="bg-amber-50/50 border-b border-amber-100 p-6 animate-fade-in">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-1.5 bg-amber-100 text-amber-700 rounded-lg">
                            <ExclamationTriangleIcon className="w-4 h-4" />
                        </div>
                        <h4 className="text-sm font-black text-amber-900 uppercase tracking-widest">Intelligence Factorization: New Scenarios Pending Approval</h4>
                    </div>
                    <div className="space-y-3">
                        {pendingRisks.map(risk => {
                            const src = (risk as any).source;
                            const sourceLabel = src === 'ai_audit'       ? { label: 'AI Audit',      color: 'bg-violet-100 text-violet-700 border-violet-200' }
                                             : src === 'event_bridge'    ? { label: 'Live Event',    color: 'bg-red-100 text-red-700 border-red-200' }
                                             : src === 'module_analysis' ? { label: 'Module Analysis', color: 'bg-blue-100 text-blue-700 border-blue-200' }
                                             :                             { label: 'System',         color: 'bg-slate-100 text-slate-600 border-slate-200' };
                            return (
                            <div key={risk.id} className="bg-white border border-amber-200 rounded-xl p-4 flex items-center justify-between shadow-sm hover:shadow-md transition-all group">
                                <div className="flex-1 min-w-0 pr-6">
                                    <div className="flex items-center gap-3 mb-1">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{risk.category}</span>
                                        <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded border ${getScoreColor(risk.score)}`}>
                                            Score: {risk.score}
                                        </span>
                                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${sourceLabel.color}`}>
                                            ⚡ {sourceLabel.label}
                                        </span>
                                    </div>
                                    <h5 className="text-sm font-bold text-slate-900 truncate">{risk.threat}</h5>
                                    <p className="text-xs text-slate-500 mt-1 italic line-clamp-1">{risk.mitigation}</p>
                                </div>
                                {!readOnly && <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => handleDiscardRisk(risk.id)}
                                        className="px-4 py-2 text-[10px] font-black uppercase text-slate-400 hover:text-red-500 transition-colors"
                                    >
                                        Discard
                                    </button>
                                    <button
                                        onClick={() => handleApproveRisk(risk.id)}
                                        className="px-6 py-2 bg-blue-600 text-white text-[10px] font-black uppercase rounded-lg hover:bg-blue-700 transition-all shadow-md shadow-blue-100"
                                    >
                                        Integrate to Register
                                    </button>
                                </div>}
                            </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Risk Table ── */}
            <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                            <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">ID</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Threat Scenario & Mitigation</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Category</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Likelihood</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Impact</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest text-center">Score</th>
                            <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Status</th>
                            <th className="px-6 py-4 text-[10px] font-black text-blue-500 uppercase tracking-widest">Remediation Path</th>
                            {showLifecycle && <th className="px-6 py-4 text-[10px] font-black text-slate-500 uppercase tracking-widest">Lifecycle Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {activeRisks.length === 0 ? (
                            <tr>
                                <td colSpan={showLifecycle ? 9 : 8} className="px-6 py-16 text-center">
                                    <div className="flex flex-col items-center gap-3 text-slate-400">
                                        <ShieldExclamationIcon className="w-10 h-10 opacity-20" />
                                        <p className="text-sm font-semibold">No reviewed risks available yet</p>
                                        <p className="text-xs opacity-70">{readOnly ? 'Complete the framework assessments and have their risk scenarios reviewed to populate this register.' : <>Review pending scenarios, click <strong>Add Risk</strong>, or run a threat analysis to populate the register.</>}</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            activeRisks.map((risk) => {
                                const catCfg = CATEGORY_CONFIG[risk.category] || { color: 'border-slate-300 bg-slate-50 text-slate-600', icon: null };
                                return (
                                    <tr key={risk.id} id={`srm-risk-${risk.id}`} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors group">
                                        <td className="px-6 py-4 text-xs font-bold text-slate-400">{risk.id}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{risk.threat}</span>
                                                <span className="text-[11px] text-slate-500 mt-0.5 leading-relaxed italic">{risk.mitigation}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase ${catCfg.color}`}>
                                                {risk.category}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`text-[10px] font-bold ${risk.likelihood === 'Critical' || risk.likelihood === 'High' ? 'text-red-600' : 'text-slate-600'}`}>
                                                {risk.likelihood}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`text-[10px] font-bold ${risk.impact === 'Critical' || risk.impact === 'High' ? 'text-red-600' : 'text-slate-600'}`}>
                                                {risk.impact}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`inline-block min-w-[2.5rem] py-1 px-2 rounded text-xs font-black border text-center ${getScoreColor(risk.score)}`}>
                                                {risk.score}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                {risk.status === 'Mitigated' && <CheckCircleIcon className="w-4 h-4 text-emerald-500" />}
                                                {risk.status === 'Active Process' && <ArrowPathIcon className="w-4 h-4 text-blue-500 animate-spin-slow" />}
                                                {risk.status === 'Identified' && <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />}
                                                {risk.status === 'Residual'   && <FlagIcon className="w-4 h-4 text-red-500" />}
                                                {risk.status === 'Closed'     && <CheckCircleIcon className="w-4 h-4 text-slate-400" />}
                                                <span className={`text-[10px] font-black uppercase ${
                                                    risk.status === 'Mitigated' ? 'text-emerald-600' :
                                                    risk.status === 'Active Process' ? 'text-blue-600' :
                                                    risk.status === 'Identified' ? 'text-amber-600' : 'text-slate-600'
                                                }`}>
                                                    {risk.status}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {(onLaunchTool || onOpenRemediation) && (risk as any).recommendation && (risk as any).recommendation !== 'N/A' ? (
                                                <button
                                                    onClick={() => onOpenRemediation ? onOpenRemediation(risk) : onLaunchTool?.((risk as any).recommendation)}
                                                    title={(risk as any).recommendation}
                                                    className="max-w-[200px] truncate text-[10px] font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg border border-blue-200 uppercase tracking-tighter transition-all flex items-center gap-1.5 group"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 flex-shrink-0 opacity-60 group-hover:opacity-100" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                                    <span className="truncate">{(risk as any).recommendation}</span>
                                                </button>
                                            ) : (
                                                <div 
                                                    className="max-w-[200px] truncate text-[10px] font-bold text-blue-700 bg-blue-50/50 px-3 py-1.5 rounded-lg border border-blue-100 uppercase tracking-tighter" 
                                                    title={(risk as any).recommendation || 'N/A'}
                                                >
                                                    {(risk as any).recommendation || 'N/A'}
                                                </div>
                                            )}
                                        </td>
                                        {showLifecycle && <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                {risk.status === 'Identified' && (
                                                    <button 
                                                        onClick={() => handleUpdateStatus(risk.id, 'Active Process')}
                                                        disabled={busyRiskId === risk.id}
                                                        className="px-3 py-1.5 bg-blue-600 text-white text-[9px] font-black uppercase rounded-lg hover:bg-blue-700 transition-all shadow-sm"
                                                    >
                                                        {busyRiskId === risk.id ? 'Saving...' : 'Start Process'}
                                                    </button>
                                                )}
                                                {risk.status === 'Active Process' && (
                                                    <button 
                                                        onClick={() => handleUpdateStatus(risk.id, 'Mitigated')}
                                                        disabled={busyRiskId === risk.id}
                                                        className="px-3 py-1.5 bg-emerald-600 text-white text-[9px] font-black uppercase rounded-lg hover:bg-emerald-700 transition-all shadow-sm"
                                                    >
                                                        {busyRiskId === risk.id ? 'Saving...' : 'Mark Mitigated'}
                                                    </button>
                                                )}
                                                {(risk.status === 'Mitigated' || risk.status === 'Closed') && (
                                                    <span className="text-[9px] font-black text-slate-400 uppercase italic">Goal Achieved</span>
                                                )}
                                            </div>
                                        </td>}
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── Footer ── */}
            <div className="p-4 bg-blue-50/50 border-t border-blue-100">
                <div className="flex items-center justify-between text-[11px]">
                    <div className="flex items-center gap-2 text-blue-700 font-bold uppercase tracking-tighter">
                        <ArrowPathIcon className="w-3 h-3 animate-spin-slow" />
                        {alerts.length > 0 ? `${alerts.length} Active Intelligence Alerts` : activeRisks.length ? 'Reviewed Risk Register' : 'Awaiting Risk Review'}
                    </div>
                    <div className="text-slate-400">
                        {project?.locationName || 'Enterprise'}{project?.updatedAt || project?.createdAt ? ` | Updated: ${formatLocalDateTime(project.updatedAt || project.createdAt)}` : ''}
                    </div>
                </div>
            </div>

            {/* ── Add Risk Modal ── */}
            {!readOnly && isAddModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
                    <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-3xl overflow-hidden border border-slate-200 flex flex-col max-h-[90vh]">

                        {/* Modal Header */}
                        <div className="p-8 border-b border-slate-100 bg-slate-50/70 flex justify-between items-center flex-shrink-0">
                            <div>
                                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">SRM Intelligence Library</h3>
                                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">
                                    {PREDEFINED_RISKS.length} threat scenarios across {CATEGORIES.length - 1} categories
                                </p>
                            </div>
                            <button onClick={() => setIsAddModalOpen(false)} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                                <XMarkIcon className="w-6 h-6 text-slate-400" />
                            </button>
                        </div>

                        {/* Search + Category Filters */}
                        <div className="px-8 py-4 border-b border-slate-100 bg-white flex-shrink-0 space-y-3">
                            {/* Search */}
                            <div className="relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search threat scenarios..."
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 bg-slate-50"
                                />
                            </div>
                            {/* Category Pills */}
                            <div className="flex gap-2 flex-wrap">
                                {CATEGORIES.map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => setModalCategory(cat)}
                                        className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider transition-all border ${
                                            modalCategory === cat
                                                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                                : 'bg-white text-slate-500 border-slate-200 hover:border-blue-400 hover:text-blue-600'
                                        }`}
                                    >
                                        {cat}
                                        {cat !== 'All' && (
                                            <span className="ml-1 opacity-60">
                                                ({PREDEFINED_RISKS.filter(r => r.category === cat).length})
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Risk Cards */}
                        <div className="p-6 overflow-y-auto flex-grow space-y-3 custom-scrollbar">
                            {filteredLibrary.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                                    <MagnifyingGlassIcon className="w-8 h-8 mb-2 opacity-30" />
                                    <p className="text-sm">No threats match your search.</p>
                                </div>
                            ) : (
                                filteredLibrary.map((risk, i) => {
                                    const catCfg = CATEGORY_CONFIG[risk.category] || { color: 'border-slate-300 bg-slate-50 text-slate-600', icon: null };
                                    return (
                                        <div
                                            key={i}
                                            onClick={() => handleAddRisk(risk)}
                                            className="p-5 bg-white border border-slate-100 rounded-2xl hover:border-blue-400 hover:shadow-md transition-all cursor-pointer group flex items-start gap-5"
                                        >
                                            <div className={`mt-0.5 p-2.5 rounded-xl border group-hover:scale-110 transition-transform ${catCfg.color}`}>
                                                {catCfg.icon || <ShieldExclamationIcon className="w-5 h-5" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start mb-1.5">
                                                    <h4 className="font-black text-slate-900 text-sm group-hover:text-blue-600 transition-colors leading-tight pr-4">{risk.threat}</h4>
                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                        <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded border ${catCfg.color}`}>
                                                            {risk.category}
                                                        </span>
                                                        <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded ${risk.score >= 15 ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
                                                            Score: {risk.score}
                                                        </span>
                                                    </div>
                                                </div>
                                                <p className="text-xs text-slate-500 mb-3 line-clamp-2 leading-relaxed">{risk.mitigation}</p>
                                                <div className="flex items-center gap-2 pt-3 border-t border-slate-50">
                                                    <span className="text-[9px] font-black text-blue-600 uppercase tracking-wider">Recommended Tool:</span>
                                                    <span className="text-[9px] font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded">{risk.recommendation}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                                {filteredLibrary.length} of {PREDEFINED_RISKS.length} threats shown — click any card to add to register
                            </p>
                            <button
                                onClick={() => setIsAddModalOpen(false)}
                                className="px-5 py-2 text-[10px] font-black uppercase text-slate-500 hover:text-slate-800 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SrmRiskRegister;
